import {
  type UserEmailChangeRequestCreateAPIT,
  type UserT,
  type UserWithOrganisationsAndRelationshipsT,
  type UserUpdateAPIT,
} from '@ruminati/types_v2/user'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import { CommonApiV2Instance } from '@/services/common'
import { useOrganisationStore } from '@/stores/organisations'
import { useUsersStore } from '@/stores/users'

import { defaultQueryOptions } from './queryDefaults'

export const queryKeysProfile = {
  all: () => ['profile'] as const,
}

export function useProfileQuery() {
  const store = useUsersStore()
  const organisationStore = useOrganisationStore()

  return useQuery({
    queryKey: computed(() => queryKeysProfile.all()),
    queryFn: async ({ signal }) => {
      const response = await CommonApiV2Instance.get('users/me', {
        signal,
      }).json<UserWithOrganisationsAndRelationshipsT>()
      if (response.organisations !== undefined) {
        organisationStore.upsertOrganisations(response.organisations)
      }
      if (response.relationships !== undefined) {
        store.setRelationships(response.relationships)
      }
      if (response.pendingOrgInvitations !== undefined) {
        organisationStore.setPendingOrgInvitations(response.pendingOrgInvitations)
      }
      store.setProfile(response.user)
      organisationStore.initializeCurrentOrg(response.user)
      return response.user
    },
    ...defaultQueryOptions,
  })
}

export function usePatchProfile() {
  const store = useUsersStore()

  return useMutation({
    mutationFn: async (details: UserUpdateAPIT) => {
      return CommonApiV2Instance.patch('users/me', { json: details }).json<UserT>()
    },
    onSuccess: (user) => {
      store.setProfile(user)
    },
  })
}

export function useRequestEmailChange() {
  return useMutation({
    mutationFn: async (details: UserEmailChangeRequestCreateAPIT) => {
      await CommonApiV2Instance.post('users/me/change-email', { json: details })
    },
  })
}
